import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Gender } from '@xing-com/crate-common-graphql-types';
import { JobCardData } from '@xing-com/crate-dream-machine-types';
import { type FacepileActorProps } from '@xing-com/facepile';

import { PeakAndPopMembers } from '../../components/peak-and-pop';

import {
  Text,
  MemberInfoContainer,
  Link,
  ProfileImage,
  Facepile,
  MemberInfoTitle,
  Label,
} from './title-and-member-info.styled';

export type MemberInfoProps = {
  data: JobCardData;
};

export const TitleAndMemberInfo: React.FC<MemberInfoProps> = ({ data }) => {
  const [isPeakAndPopShown, setIsPeakAndPopShown] = useState(false);

  if (data.type !== 'TakenJob') {
    return (
      <Link data-qa="dm-member-info">
        <Title data={data} />
      </Link>
    );
  }

  return (
    <>
      <Link data-qa="dm-member-info" $noMargin={true}>
        <Title data={data} />
      </Link>
      <Link data-qa="dm-member-info">
        <MemberInfoContainer>
          <Images data={data} />

          <MemberInfoText data={data} />
        </MemberInfoContainer>
      </Link>

      <PeakAndPopMembers
        isShown={isPeakAndPopShown}
        setIsShown={setIsPeakAndPopShown}
        trackTileName="commonalities"
      />
    </>
  );
};

const Title: React.FC<MemberInfoProps> = ({ data }) => {
  return (
    <Text size="xxlarge" title={data.jobTitle || ''}>
      {data.jobTitle}
    </Text>
  );
};

const Images: React.FC<MemberInfoProps> = ({ data }) => {
  if (data.type !== 'TakenJob' || !data.similarMembersCount) {
    return null;
  }

  if (data.similarMembersCount === 1) {
    return (
      <DisplaySingleMemberImage
        src={data.similarMembers?.[0]?.user?.profileImage?.[0]?.url}
      />
    );
  }

  if (data?.similarMembersCount > 1) {
    return <DisplayMultipleMembersFacepile data={data} />;
  }

  return null;
};

const DisplaySingleMemberImage: React.FC<{ src?: string }> = ({ src }) => {
  return <ProfileImage size="medium" type="neutral-user" src={src} alt="" />;
};

const DisplayMultipleMembersFacepile: React.FC<MemberInfoProps> = ({
  data,
}) => {
  const members = dataCardToFacepileActorProps(data);

  return (
    <Facepile actors={members} size="large" maxActors={3} variant="condensed" />
  );
};

const dataCardToFacepileActorProps = (
  data: JobCardData
): FacepileActorProps[] => {
  const members = data.similarMembers?.filter(
    (member) => member?.user?.id !== undefined
  );
  if (!members) {
    return [];
  }

  return members.map((member) => {
    return {
      id: member?.user?.id || '',
      profileName: member?.user?.displayName,
      src: member?.user?.profileImage?.[0]?.url,
      type:
        member?.user?.gender === Gender.Male
          ? 'male-user'
          : member?.user?.gender === Gender.Female
            ? 'female-user'
            : 'neutral-user',
      loading: 'lazy',
      href: member?.user?.pageName ? `/profile/${member?.user?.pageName}` : '#',
    };
  });
};

const MemberInfoText: React.FC<MemberInfoProps> = ({ data }) => {
  const members = data.similarMembers;
  const isSingle = data.similarMembersCount === 1;

  return (
    <div>
      <MemberInfoTitle size="small">
        {isSingle ? (
          members?.[0]?.user?.displayName
        ) : (
          <>
            <span>{data.similarMembersCount} </span>
            <FormattedMessage
              id={'DREAM_MACHINE_JOB_CARD_OCCUPANTS_ROW_TITLE'}
            />
          </>
        )}
      </MemberInfoTitle>
      <Label size="small" lineHeight="dense">
        <FormattedMessage
          id={
            isSingle
              ? 'DREAM_MACHINE_JOB_CARD_OCCUPANTS_ROW_SUBTITLE_SINGLE_WEB'
              : 'DREAM_MACHINE_JOB_CARD_OCCUPANTS_ROW_SUBTITLE_MULTIPLE'
          }
          values={{
            location: members?.[0]?.user?.location?.city,
          }}
        />
      </Label>
    </div>
  );
};
